import Button from "@components/common/Button";
import CustomAccordion from "@components/common/CustomAccordion";
import EditIcon from "@components/icons/EditIcon";
import { ModalConfigurationBUClientVessel } from "@components/Modals/ModalConfigurationBUClientVessel";
import { useIsMobile } from "@components/mediaQuery/useIsMobile";
import { getConfigurationByVesselId } from "@services/accountMangement/accountNumbersService";
import { checkPermission } from "@utils/permissions";
import { Card } from "flowbite-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const ActivationGeneralConfig = ({ id, canEdit, canUpdate = false }) => {
	const typeEntity = "vessels";
	const [openAccordions, setOpenAccordions] = useState({
		activation: true,
	});
	const [configData, setConfigData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [openModal, setOpenModal] = useState(false);
	const [selectedRowData, setSelectedRowData] = useState(null);
	const token = useSelector((state) => state.auth.user.token);
	const { permissions } = useSelector((state) => state.auth.user);
	const vesselsSite = permissions.find((resource) => resource.resource_name === "vessels_site");
	const authorizedPermissions = vesselsSite?.permissions || [];

	const canAddActivationConfig = checkPermission(
		authorizedPermissions,
		"add new vessel configuration",
	);
	const canUpdateconfig = checkPermission(authorizedPermissions, "update vessel configuration");

	const fetchConfigurations = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await getConfigurationByVesselId(id, token);
			setConfigData(response.data);
		} catch (err) {
			console.error("Error fetching configuration:", err);
			setError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetchConfigData = async () => {
			try {
				setLoading(true);
				setError(null);
				const token = localStorage.getItem("token");
				const response = await getConfigurationByVesselId(id, token);
				setConfigData(response.data);
			} catch (err) {
				console.error("Error fetching configuration:", err);
				setError(err);
			} finally {
				setLoading(false);
			}
		};

		fetchConfigData();
	}, [id]);

	const toggleAccordion = (accordionId) => {
		setOpenAccordions((prevState) => ({
			...prevState,
			[accordionId]: !prevState[accordionId],
		}));
	};
	const handleEditClick = (rowData) => {
		setSelectedRowData(rowData);
		setOpenModal(true);
	};
	const showButton = canAddActivationConfig && canEdit;
	const isMobile = useIsMobile();

	return (
		<div className="space-y-4">
			<CustomAccordion
				id="activation"
				title="ACTIVATION GENERAL CONFIGURATION"
				isOpen={openAccordions.activation}
				toggleAccordion={() => toggleAccordion("activation")}
				showButton={showButton}
				isMobile={isMobile}
				onNewConfiguration={() => {
					setSelectedRowData(null);
					setOpenModal(true);
				}}
			>
				{loading && <p className="text-gray-400 text-sm">Loading...</p>}

				{!loading && error && <p className="text-red-500 text-sm">Error: {error}</p>}

				{!loading && !error && configData.length === 0 && (
					<p className="text-center text-gray-400">No configuration data available.</p>
				)}

				{!loading &&
					!error &&
					configData.length > 0 &&
					configData.map((config) => (
						<div className="relative mb-2 flex flex-col">
							<Card
								theme={{
									root: {
										base: `
											flex rounded-2xl bg-[#727DA333] hover:bg-[#727DA366]
											transition-colors duration-200 shadow-md
										`,
									},
								}}
								className="w-full p-4"
							>
								<div className="flex flex-col gap-4">
									{/* Header - Config Reference & Status */}
									<div className="flex flex-col gap-2 border-[#727DA366] border-b border-dotted pb-3 sm:flex-row sm:items-center sm:justify-between">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-bold text-gray-300 text-sm uppercase tracking-wide">
												CONFIG REF: {config?.reference_id}
											</span>
											{canUpdate && (
												<Button
													text=""
													icon={EditIcon}
													onClick={() => handleEditClick(config)}
													className="!p-2 rounded-lg text-sm text-white hover:bg-gray-800"
													classNameIcon="!w-4 !h-4"
													color="blue"
													disabled={!canUpdateconfig}
												/>
											)}
										</div>

										<span
											className={`flex w-fit items-center rounded-lg px-3 py-1 font-semibold text-xs ${
												config?.status === 1
													? "border border-green-400 bg-green-900/30 text-green-400"
													: "border border-red-400 bg-red-900/30 text-red-400"
											}`}
										>
											{config?.status === 1 ? "ACTIVE" : "INACTIVE"}
										</span>
									</div>

									{/* Content Info */}
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
										<div className="flex justify-between">
											<span className="text-gray-400 text-sm">ACCOUNT TYPE</span>
											<span className="font-medium text-gray-200 text-sm">{config?.type}</span>
										</div>

										<div className="flex justify-between">
											<span className="text-gray-400 text-sm">ACCOUNT NUMBER</span>
											<span className="font-medium text-gray-200 text-sm">
												{config?.account_number?.number}
											</span>
										</div>

										<div className="flex justify-between">
											<span className="text-gray-400 text-sm">NICKNAME</span>
											<span className="font-medium text-gray-200 text-sm">
												{config?.account_number?.nickname}
											</span>
										</div>

										<div className="flex justify-between">
											<span className="text-gray-400 text-sm">SUBSCRIPTION PLAN</span>
											<span className="text-right font-medium text-gray-200 text-sm">
												{config?.vessel_subscriptions.map((item) => item.name).join(", ")}
											</span>
										</div>
									</div>
								</div>
							</Card>
						</div>
					))}
			</CustomAccordion>

			<ModalConfigurationBUClientVessel
				bu_client_id={id}
				isOpen={openModal}
				selectedRowData={selectedRowData}
				closeModal={() => setOpenModal(false)}
				token={token}
				fetchConfigurations={fetchConfigurations}
				typeEntity={typeEntity}
			/>
		</div>
	);
};

export default ActivationGeneralConfig;
